import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import tarotCards from "./tarotCards";
import TarotCard from "./components/TarotCard";
import DailyReading from "./components/DailyReading";
import { themeEmojis } from "./utils/themeEmojis";
import BuildNotes from "./components/BuildNotes";
import { useZodiacStore } from "./store/useZodiacStore";

// Generate a poetic, adaptive summary transition line
function generateJourneyLine(focus) {
  const spiritual = "Spirit speaks through time — past, present, future.";
  const love = "Heart's journey — memories, feelings, hopes.";
  const health = "Body and spirit — resilience, presence, balance.";
  const creativity = "Creative path — sparks, flow, possibility.";
  const career = "Professional journey — preparation, action, growth.";

  const general = "Your path unfolds — past, present, future.";

  switch (focus?.toLowerCase()) {
    case "spirituality":
      return spiritual;
    case "love":
      return love;
    case "health":
      return health;
    case "creativity":
      return creativity;
    case "career":
      return career;
    default:
      return general;
  }
}

function getRandomCards(deck, count = 3) {
  const shuffled = [...deck].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
}

function generateSummary(cards, focus = "") {
  if (cards.length !== 3) return "";

  const [past, present, future] = cards;
  const journeyLine = generateJourneyLine(focus);

  // Use fallback meaning if specific narrative is missing
  const pastText = past.narrative?.past || past.meaning || "";
  const presentText = present.narrative?.present || present.meaning || "";
  const futureText = future.narrative?.future || future.meaning || "";

  // Structured narrative linking text to card and position
  const baseNarrative = [
    `Past: ${past.name} — ${pastText}`,
    `Present: ${present.name} — ${presentText}`,
    `Future: ${future.name} — ${futureText}`,
  ]
    .filter(Boolean)
    .join("\n\n");

  const allThemes = [
    ...(past.theme || []),
    ...(present.theme || []),
    ...(future.theme || []),
  ];

  const themeCounts = allThemes.reduce((acc, theme) => {
    acc[theme] = (acc[theme] || 0) + 1;
    return acc;
  }, {});

  const sortedThemes = Object.entries(themeCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([theme]) => theme);

  const dominantThemes = sortedThemes.filter(
    (theme) => themeCounts[theme] >= 2
  );

  const decoratedThemes = dominantThemes.map(
    (theme) => `${themeEmojis[theme] || "✨"} ${theme}`
  );

  let comboInsight = "";
  const comboCheck = allThemes.join(",");

  if (comboCheck.includes("freedom") && comboCheck.includes("control")) {
    comboInsight = "⚖️ Balance structure with spontaneity.";
  } else if (
    comboCheck.includes("transformation") &&
    comboCheck.includes("rebirth")
  ) {
    comboInsight = "🌅 Powerful shift ahead — release the old.";
  } else if (comboCheck.includes("truth") && comboCheck.includes("illusion")) {
    comboInsight = "🌓 Hidden truth will be revealed.";
  } else if (comboCheck.includes("love") && comboCheck.includes("choice")) {
    comboInsight = "💞 Heart-centered decision coming.";
  }

  // --- Summary Assembly Logic ---

  // 1. Get the base text for themes (without leading newlines)
  const baseThemeText = decoratedThemes.length
    ? `Key themes: ${decoratedThemes.join(", ")}`
    : `Each card reveals a distinct influence.`;

  // 2. Get the base text for focus (without leading newlines)
  const baseFocusText = focus ? `Focus: ${focus}` : "";

  // 3. Determine if the fallback theme text and focus text should be combined
  const shouldCombine = !decoratedThemes.length && focus;

  let formattedThemeChunk = "";
  let formattedFocusChunk = "";

  if (shouldCombine) {
    formattedThemeChunk = `\n\n${baseThemeText} ${baseFocusText}`;
    formattedFocusChunk = "";
  } else {
    if (baseThemeText) {
      formattedThemeChunk = `\n\n${baseThemeText}`;
    }
    if (baseFocusText) {
      formattedFocusChunk = `\n\n${baseFocusText}`;
    }
  }

  // 4. Format combo insight (always a separate paragraph if it exists)
  const formattedComboInsight = comboInsight ? `\n\n${comboInsight}` : "";

  // 5. Assemble the final summary string
  return `${journeyLine}${
    baseNarrative ? `\n\n${baseNarrative}` : ""
  }${formattedThemeChunk}${formattedComboInsight}${formattedFocusChunk}`;
}

export default function TarotReader() {
  const [drawnCards, setDrawnCards] = useState([]);
  const [focus, setFocus] = useState("");
  const [aiReading, setAiReading] = useState("");
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [aiError, setAiError] = useState("");
  const [showFullReading, setShowFullReading] = useState(false);
  const [fullReading, setFullReading] = useState("");
  const [isLoadingFullReading, setIsLoadingFullReading] = useState(false);
  const [justDrewCards, setJustDrewCards] = useState(false);
  const [showBuildNotes, setShowBuildNotes] = useState(false);
  const [isShuffling, setIsShuffling] = useState(false);

  // Get selected signs from store
  const selectedZodiac = useZodiacStore((state) => state.selectedZodiac);
  const selectedRisingSign = useZodiacStore(
    (state) => state.selectedRisingSign
  );
  // Get actions to set signs
  const setSelectedZodiac = useZodiacStore((state) => state.setSelectedZodiac);
  const setSelectedRisingSign = useZodiacStore(
    (state) => state.setSelectedRisingSign
  );

  // Reset signs on component mount
  useEffect(() => {
    setSelectedZodiac(""); // Reset sun sign
    setSelectedRisingSign(""); // Reset rising sign
  }, [setSelectedZodiac, setSelectedRisingSign]); // Include setters in dependency array

  // Function to toggle build notes view
  const toggleBuildNotes = () => {
    setShowBuildNotes(!showBuildNotes);
    // Scroll to top when switching views
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDraw = () => {
    // Reset relevant states
    setAiReading("");
    setAiError("");
    setShowFullReading(false);
    setFullReading("");
    setDrawnCards([]); // Clear previous cards immediately
    setIsShuffling(true); // Start shuffling animation

    // Simulate shuffling time
    setTimeout(() => {
      // Draw new cards (logic restored here)
      const initialCards = getRandomCards(tarotCards);
      if (initialCards.length > 0) {
        console.log(
          "Reversed meaning of first drawn card BEFORE map:",
          initialCards[0]?.reversedMeaning
        );
      }
      const newCards = initialCards.map((card) => ({
        ...card,
        isReversed: Math.random() < 0.23,
      }));

      setDrawnCards(newCards);
      setJustDrewCards(true); // Set this after cards are updated

      // Fetch AI reading immediately
      if (newCards.length === 3) {
        getAIReading(newCards, focus, selectedZodiac, selectedRisingSign);
      }

      setIsShuffling(false); // Stop shuffling animation
    }, 2800); // Keep the 2.8 second delay
  };

  const getAIReading = async (
    cards = drawnCards,
    currentFocus = focus,
    sunSign = selectedZodiac,
    risingSign = selectedRisingSign
  ) => {
    if (cards.length !== 3) return;

    setIsLoadingAI(true);
    setAiReading(""); // Ensure reset before fetch
    setAiError("");

    // Scroll to reading area slightly after triggering load
    // (might need adjustment based on layout changes)
    setTimeout(() => {
      const readingArea = document.getElementById("reading-content-area");
      if (readingArea) {
        readingArea.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);

    try {
      // Keep the detailed card descriptions including Astrology/Qabalah
      const cardsDescription = cards
        .map((card, index) => {
          const position = ["past", "present", "future"][index];
          // Determine name and meaning based on reversal
          const displayName = card.isReversed
            ? `${card.name} (Reversed)`
            : card.name;
          const displayMeaning = card.isReversed
            ? card.reversedMeaning
            : card.meaning;

          // Start description with position and adjusted name/meaning
          let description = `${
            position.charAt(0).toUpperCase() + position.slice(1)
          } card: ${displayName} - ${displayMeaning || "Meaning unavailable"}`;

          if (card.astrology) {
            const { zodiacSign, planet, element, modality } = card.astrology;
            let astroParts = [];
            if (zodiacSign) astroParts.push(`Sign: ${zodiacSign}`);
            if (planet) astroParts.push(`Planet: ${planet}`);
            if (element) astroParts.push(`Element: ${element}`);
            if (modality) astroParts.push(`Modality: ${modality}`);
            if (astroParts.length > 0) {
              description += ` (Astrology: ${astroParts.join(", ")})`;
            }
            // No longer need the alignment check here, handled in prompt globally
          }

          if (card.qabalah && card.qabalah.path !== null) {
            const { path, hebrewLetter, gematria } = card.qabalah;
            let qabalahParts = [];
            if (path) qabalahParts.push(`Path: ${path}`);
            if (hebrewLetter) qabalahParts.push(`Letter: ${hebrewLetter}`);
            if (gematria) qabalahParts.push(`Gematria: ${gematria}`);
            if (qabalahParts.length > 0) {
              description += ` (Qabalah: ${qabalahParts.join(", ")})`;
            }
          } else if (card.qabalah && card.qabalah.path) {
            description += ` | Qabalah: Sephirah ${card.qabalah.path}`;
          }

          return description;
        })
        .join("\n");

      // Updated prompt with selected zodiac sign and rising sign
      const prompt = `You are a sharp, witty tarot reader. Provide a concise reading (3 clear, connected sentences per time period) based on these 3 tarot cards (past, present, future). IMPORTANT: Note if a card includes "(Reversed)" in its description - use its provided reversed meaning and interpret its influence accordingly.

      Subtly weave in the provided Astrological (sign, planet, element) and Qabalistic (path, letter for Majors/Sephirah for Minors) details for each card to add depth to its meaning in that position. Connect it to the interpretation, don't just list data.
      
      ${
        sunSign
          ? `The querent's sun sign is ${sunSign}. Consider how this core identity energy might influence the themes.`
          : ""
      }
      ${
        risingSign
          ? `The querent's rising sign is ${risingSign}. This influences their outward persona and initial approach to life's situations. Consider how this might subtly color their journey through the reading's timeline (past approach, present interactions, future outlook).`
          : ""
      }

      Focus area: ${currentFocus || "General life path"}
      
      Cards:
      ${cardsDescription}
      
      Format your response exactly like this, keeping the titles and structure:
      
      [Focus] Reading
      
      **PAST:** [Three connected sentences about the past, subtly integrating its card's astro/qabalah insights and potentially the rising sign's influence on past approach.]
      
      **PRESENT:** [Three connected sentences about the present, subtly integrating its card's astro/qabalah insights and potentially the rising sign's influence on current interactions/persona.]
      
      **FUTURE:** [Three connected sentences about the future, subtly integrating its card's astro/qabalah insights and potentially the rising sign's influence on future outlook/approach.]
      
      **In summary:** [Three to four sentences that tie everything together and provide guidance, reflecting the overall energy including any personal sun/rising sign influences.]
      
      Keep it direct, actionable, and poetic.`;

      const messages = [
        {
          role: "system",
          content:
            "You are a sharp, witty tarot reader providing concise, actionable readings that subtly integrate astrological (card and personal signs) and qabalistic card details.",
        },
        { role: "user", content: prompt },
      ];

      const isLocalhost = window.location.hostname === "localhost";

      let apiUrl = isLocalhost
        ? "http://localhost:3001/api/reading"
        : "/api/reading";

      try {
        console.log("Attempting to call API at:", apiUrl);
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            cards: cards, // Use passed cards
            focus: currentFocus || "General", // Use passed focus
            messages,
          }),
        });

        if (!response.ok) {
          let errorMessage = "API request failed";
          try {
            const errorData = await response.json();
            errorMessage =
              errorData.error?.message || errorData.error || errorMessage;
          } catch (e) {
            errorMessage = (await response.text()) || errorMessage;
          }

          console.error("API Error:", errorMessage);
          throw new Error(errorMessage);
        }

        const data = await response.json();

        const readingText =
          data.reading ||
          (data.choices && data.choices[0]?.message?.content) ||
          "";
        setAiReading(readingText.trim());
        return;
      } catch (initialError) {
        console.warn("Initial API call failed:", initialError);

        if (!isLocalhost) {
          throw new Error(
            `API service unavailable. If you're the site owner, please check your OpenAI API key configuration.`
          );
        }

        console.log("Trying direct OpenAI call fallback...");

        let apiKey = localStorage.getItem("openai_api_key");
        if (!apiKey) {
          apiKey = window.prompt(
            "Local API server unavailable. Enter your OpenAI API key for direct call (will be saved in browser):"
          );
          if (apiKey) localStorage.setItem("openai_api_key", apiKey);
          else throw new Error("API key is required for direct OpenAI call");
        }

        const directResponse = await fetch(
          "https://api.openai.com/v1/chat/completions",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${apiKey.trim()}`,
            },
            body: JSON.stringify({
              model: "gpt-3.5-turbo",
              messages,
              max_tokens: 1000,
              temperature: 0.7,
            }),
          }
        );

        if (!directResponse.ok) {
          const errorData = await directResponse.json().catch(() => ({}));
          throw new Error(
            errorData.error?.message || "Direct OpenAI API call failed"
          );
        }

        const directData = await directResponse.json();
        const directReadingText = directData.choices[0].message.content.trim();
        setAiReading(directReadingText);
      }
    } catch (err) {
      console.error("Error getting AI reading:", err);
      setAiError(err.message || "Failed to get AI reading. Please try again.");
    } finally {
      setIsLoadingAI(false);
    }
  };

  const getFullReading = async () => {
    if (drawnCards.length !== 3) return;

    setIsLoadingFullReading(true);
    setFullReading("");

    // Get current sign values from store directly for this call
    const sunSign = useZodiacStore.getState().selectedZodiac;
    const risingSign = useZodiacStore.getState().selectedRisingSign;

    try {
      // Keep the detailed card descriptions including Astrology/Qabalah
      const cardsDescription = drawnCards
        .map((card, index) => {
          const position = ["past", "present", "future"][index];
          // Determine name and meaning based on reversal
          const displayName = card.isReversed
            ? `${card.name} (Reversed)`
            : card.name;
          const displayMeaning = card.isReversed
            ? card.reversedMeaning
            : card.meaning;

          // Start description with position and adjusted name/meaning
          let description = `${
            position.charAt(0).toUpperCase() + position.slice(1)
          } card: ${displayName} - ${displayMeaning || "Meaning unavailable"}`;

          if (card.astrology) {
            const { zodiacSign, planet, element, modality } = card.astrology;
            let astroParts = [];
            if (zodiacSign) astroParts.push(`Sign: ${zodiacSign}`);
            if (planet) astroParts.push(`Planet: ${planet}`);
            if (element) astroParts.push(`Element: ${element}`);
            if (modality) astroParts.push(`Modality: ${modality}`);
            if (astroParts.length > 0) {
              description += ` | Astrology: ${astroParts.join(", ")}`;
            }
            // No longer need the alignment check here, handled in prompt globally
          }

          if (card.qabalah && card.qabalah.path !== null) {
            const { path, hebrewLetter, gematria } = card.qabalah;
            let qabalahParts = [];
            if (path) qabalahParts.push(`Path ${path}`);
            if (hebrewLetter) qabalahParts.push(`Letter ${hebrewLetter}`);
            if (gematria) qabalahParts.push(`Gematria ${gematria}`);
            if (qabalahParts.length > 0) {
              description += ` | Qabalah: ${qabalahParts.join(", ")}`;
            }
          } else if (card.qabalah && card.qabalah.path) {
            description += ` | Qabalah: Sephirah ${card.qabalah.path}`;
          }

          return description;
        })
        .join("\n");

      // Construct the personal influence text separately to avoid nested template literals
      let personalInfluenceText = "";
      if (sunSign || risingSign) {
        const sunText = sunSign ? `Sun sign (${sunSign})` : "";
        const risingText = risingSign ? `Rising sign (${risingSign})` : "";
        const separator = sunSign && risingSign ? " and " : "";
        const signPart = `${sunText}${separator}${risingText}`;

        const sunInfluence = sunSign
          ? "Sun sign affects core motivations/identity and the "
          : "";
        const risingInfluence = risingSign
          ? "Rising sign shapes their approach/first impressions "
          : "";
        const influenceDetails = `${sunInfluence}${risingInfluence}`.trim(); // Trim potential trailing space

        personalInfluenceText = `Discuss how the querent's ${signPart} might interact with the reading's major themes. Focus on how the ${influenceDetails} in relation to the cards drawn.`;
      } else {
        personalInfluenceText =
          "Briefly touch upon the general astrological energies present in the cards drawn, as no personal signs were provided.";
      }

      // Updated prompt for full reading using the constructed personalInfluenceText
      const prompt = `You are a wise and eloquent tarot reader providing a comprehensive interpretation. Analyze these three cards (past, present, future) in depth, addressing the focus area: ${
        focus || "General life path"
      }.

      **IMPORTANT:** Note if a card includes "(Reversed)" in its description - use its provided reversed meaning and interpret its influence accordingly.

      **Detailed Instructions:**
      1.  **Past Card (${
        drawnCards[0].name
      }):** Analyze its meaning, considering the querent's potential past approach influenced by their ${
        risingSign ? `Rising Sign (${risingSign})` : "general tendencies"
      }. Integrate the card's specific astro/qabalah details.
      2.  **Present Card (${
        drawnCards[1].name
      }):** Analyze its meaning, reflecting on how the querent's ${
        sunSign ? `Sun Sign (${sunSign}) core energy` : "current state"
      } and ${
        risingSign
          ? `Rising Sign (${risingSign}) outward persona`
          : "presentation"
      } might interact with the card's themes. Integrate the card's specific astro/qabalah details.
      3.  **Future Card (${
        drawnCards[2].name
      }):** Analyze its potential outcome, considering how the querent might navigate this future influenced by their ${
        sunSign ? `Sun (${sunSign})` : "core"
      } and ${
        risingSign ? `Rising (${risingSign})` : "approach"
      } signs. Integrate the card's specific astro/qabalah details.
      4.  **Overall Narrative & Guidance:** Synthesize the insights from the past, present, and future, weaving together the card meanings and the subtle influences of the querent's provided signs (${
        sunSign || "N/A"
      } Sun, ${risingSign || "N/A"} Rising) into actionable guidance.
      5.  **Personal Astrological Influence:** ${personalInfluenceText} // Use the pre-constructed string
      
      **Tone:** Wise, insightful, slightly mystical, but grounded and practical.
      
      **Format MANDATORY:** 
      - Start with a title line: # In-Depth [Focus] Reading
      - Follow with a 1-2 sentence introduction paragraph.
      - For EACH section (Past, Present, Future, Narrative, Astrological Influence), you MUST include a header line **exactly** like this on its own line: **Section Title: Card Name** (e.g., **Past: The Fool**, **Present: The Lovers**, **Future: Ten of Pentacles**, **Integration and Guidance:**, **Personal Astrological Influence:**). Use the actual card names for Past/Present/Future headers. Do NOT use markdown ## for these headers.
      - After each header line, provide the analysis in regular paragraphs.
      - Use markdown **bold** only for the exact header lines specified above.
      
      **Cards Drawn:**
      ${cardsDescription}
      `;

      const messages = [
        {
          role: "system",
          content:
            "You are a wise tarot reader providing in-depth, structured readings integrating card details and personal astrological context (Sun and Rising signs).",
        },
        { role: "user", content: prompt },
      ];

      const isLocalhost = window.location.hostname === "localhost";

      let apiUrl = isLocalhost
        ? "http://localhost:3001/api/reading"
        : "/api/reading";

      try {
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            cards: drawnCards,
            focus: focus || "General",
            messages,
            isDetailedReading: true,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.error?.message || errorData.error || "API request failed"
          );
        }

        const data = await response.json();

        const readingText =
          data.reading ||
          (data.choices && data.choices[0]?.message?.content) ||
          "";
        setFullReading(readingText.trim());
        return;
      } catch (initialError) {
        console.warn("Initial API call failed:", initialError);

        if (!isLocalhost) throw initialError;

        console.log("Trying direct OpenAI call fallback...");

        let apiKey = localStorage.getItem("openai_api_key");
        if (!apiKey) {
          apiKey = window.prompt(
            "Local API server unavailable. Enter your OpenAI API key for direct call (will be saved in browser):"
          );
          if (apiKey) localStorage.setItem("openai_api_key", apiKey);
          else throw new Error("API key is required for direct OpenAI call");
        }

        const directResponse = await fetch(
          "https://api.openai.com/v1/chat/completions",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${apiKey.trim()}`,
            },
            body: JSON.stringify({
              model: "gpt-3.5-turbo",
              messages,
              max_tokens: 2000,
              temperature: 0.7,
            }),
          }
        );

        if (!directResponse.ok) {
          const errorData = await directResponse.json().catch(() => ({}));
          throw new Error(
            errorData.error?.message || "Direct OpenAI API call failed"
          );
        }

        const directData = await directResponse.json();
        const directReadingText = directData.choices[0].message.content.trim();
        setFullReading(directReadingText);
      }
    } catch (err) {
      console.error("Error getting full reading:", err);
      setFullReading(
        "Sorry, we couldn't generate a detailed reading at this time. Please try again later."
      );
    } finally {
      setIsLoadingFullReading(false);
    }
  };

  useEffect(() => {
    if (justDrewCards && drawnCards.length === 3) {
      const isMobile = window.innerWidth < 768;
      if (isMobile) {
        setTimeout(() => {
          const cardContainer = document.getElementById("card-grid-container");
          if (cardContainer) {
            cardContainer.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
      }
      setJustDrewCards(false);
    }
  }, [drawnCards, justDrewCards]);

  return (
    <div className="min-h-screen bg-white text-black px-4 pt-4 pb-10 flex flex-col items-start font-sans">
      {/* Conditionally render Build Notes or Main Content */}
      {showBuildNotes ? (
        <BuildNotes onClose={toggleBuildNotes} />
      ) : (
        <>
          {/* Added Title */}
          <div className="w-full max-w-6xl mb-8">
            {/* Updated Links */}
            <Link
              to="/"
              className="text-xs text-black mb-1 uppercase font-bold hover:underline underline"
            >
              HOME
            </Link>
            <span className="text-xs text-gray-500 mx-2">/</span>
            <button
              onClick={toggleBuildNotes}
              className="text-xs text-custom-red mb-1 uppercase font-bold hover:underline underline"
            >
              APP BUILD NOTES
            </button>
          </div>

          {/* Header Section - Align Left */}
          <div className="w-full max-w-6xl flex flex-col items-start">
            <motion.header
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="mb-10"
            >
              <h1 className="text-3xl md:text-4xl font-bold text-black mb-4 lowercase">
                tarot into your past, present and future
              </h1>
              <p className="text-base md:text-lg text-black">
                Select a focus for your three card reading...
              </p>
            </motion.header>
          </div>

          {/* Main Content Section - Align Left */}
          <div className="w-full max-w-6xl flex flex-col lg:flex-row gap-10 lg:gap-16">
            {/* Left Column: Focus Buttons */}
            <div className="w-full lg:w-1/4 lg:flex-shrink-0 flex flex-col items-center lg:items-start">
              <div className="w-full flex flex-col items-center md:items-start space-y-3">
                {[
                  "General",
                  "Love",
                  "Career",
                  "Health",
                  "Spirituality",
                  "Creativity",
                ].map((f, index) => (
                  <motion.button
                    key={f}
                    onClick={() => setFocus(f)}
                    className={`w-full max-w-xs px-4 py-2 border-2 transition-colors duration-300 uppercase text-sm
                      ${
                        focus === f
                          ? "bg-custom-red border-custom-red text-white"
                          : "bg-white border-black text-black hover:bg-custom-red hover:border-custom-red hover:text-white"
                      }`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.4,
                      ease: "easeOut",
                      delay: index * 0.07,
                    }}
                  >
                    {f}
                  </motion.button>
                ))}

                {/* --- Main Action Button --- */}
                {/* Show this button only when no cards are drawn */}
                {drawnCards.length === 0 && (
                  <motion.button
                    onClick={handleDraw}
                    // Disable button if no focus is selected
                    disabled={!focus}
                    className={`mt-4 px-8 py-3 text-sm w-full max-w-xs uppercase rounded-none transition-all duration-300
                    ${
                      !focus
                        ? "bg-gray-300 text-black font-bold cursor-not-allowed" // Disabled state with black bold text
                        : "bg-black text-white hover:scale-105" // Enabled state
                    }`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.4,
                      ease: "easeOut",
                      delay: 6 * 0.07, // Maintain delay
                    }}
                  >
                    {/* Text changes based on focus selection */}
                    {focus ? "Draw Cards" : "Select Focus"}
                  </motion.button>
                )}

                {/* --- Clear Button --- */}
                {/* Show this button only when cards ARE drawn */}
                {drawnCards.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.4,
                      ease: "easeOut",
                      delay: 8 * 0.07, // Reverted delay
                    }}
                  >
                    <button
                      onClick={() => {
                        setFocus("");
                        setDrawnCards([]);
                        setAiReading("");
                        setAiError("");
                        setShowFullReading(false);
                        setFullReading("");
                        // Also reset signs if needed, though they persist from HomePage
                        // setSelectedZodiac("");
                        // setSelectedRisingSign("");
                      }}
                      className="mt-2 text-custom-red hover:text-black uppercase flex items-center justify-center md:justify-start w-full max-w-xs"
                    >
                      <span className="mr-1">✖</span> Clear & Reset
                    </button>
                  </motion.div>
                )}
              </div>
            </div>

            {/* Right Column: Card Grid / Summary - Adjusted width */}
            <div className="w-full lg:w-3/4">
              {/* --- Shuffling Animation --- */}
              {isShuffling && (
                <motion.div
                  className="flex flex-col items-center justify-center h-64 text-center relative" // Added relative positioning for cards
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <p className="text-lg text-gray-700 mb-8 italic">
                    {" "}
                    {/* Increased bottom margin */}
                    Shuffling the deck...
                  </p>
                  {/* Container for the animated cards */}
                  <div className="relative h-16 w-48">
                    {" "}
                    {/* Container for cards */}
                    {/* --- Three Card Shuffle Animation (Restored) --- */}
                    {[0, 1, 2].map((i) => {
                      const cardStyle =
                        "absolute top-0 h-16 w-10 bg-transparent border-2 border-black rounded shadow-lg";
                      let initialLeft = "40%";
                      let animateX = [0, 40, -40, 0];
                      if (i === 0) {
                        initialLeft = "10%";
                        animateX = [0, 80, -20, 30];
                      } else if (i === 1) {
                        initialLeft = "70%";
                        animateX = [0, -80, 20, -30];
                      }
                      return (
                        <motion.div
                          key={i}
                          className={cardStyle}
                          style={{ left: initialLeft }}
                          initial={{ x: 0, opacity: 1 }}
                          animate={{ x: animateX, opacity: [1, 0.3, 0.3, 1] }}
                          transition={{
                            duration: 1.2, // Keep the 1.2s animation speed
                            repeat: Infinity,
                            repeatType: "loop",
                            ease: "easeInOut",
                            delay: i * 0.1,
                          }}
                        />
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* --- Card Grid --- */}
              {!isShuffling && drawnCards.length === 3 && (
                <div
                  id="card-grid-container"
                  className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10 mb-10"
                >
                  {drawnCards.map((card, index) => (
                    <TarotCard
                      key={card.id}
                      card={card}
                      index={index}
                      position={["Past", "Present", "Future"][index]}
                    />
                  ))}
                </div>
              )}

              {/* --- Reading Content Area --- */}
              {!isShuffling && drawnCards.length === 3 && (
                <motion.div
                  id="reading-content-area"
                  className="w-full text-left"
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: isLoadingAI || aiReading || aiError ? 1 : 0,
                  }}
                  transition={{ delay: 0.2 }}
                >
                  {isLoadingAI && (
                    <div className="mb-6 text-center">
                      <p className="text-gray-600 italic">
                        Conjuring your reading...
                      </p>
                      <div className="mt-2 flex justify-center">
                        <div className="animate-spin h-8 w-8 border-b-2 border-gray-800"></div>
                      </div>
                    </div>
                  )}

                  {aiError && (
                    <div className="mb-6 p-3 bg-red-50 border border-red-200">
                      <p className="text-red-600 mb-2">
                        Error getting AI reading:
                      </p>
                      <p className="text-sm">{aiError}</p>
                      <button
                        onClick={() =>
                          getAIReading(
                            drawnCards,
                            focus,
                            selectedZodiac,
                            selectedRisingSign
                          )
                        } // Allow retry
                        className="mt-3 px-3 py-1 bg-gray-200 text-sm hover:bg-gray-300"
                      >
                        Try Again
                      </button>
                    </div>
                  )}

                  {aiReading && !isLoadingAI && !aiError && (
                    <div className="mb-6">
                      <h2 className="text-2xl font-bold mb-2 text-gray-800 uppercase">
                        {focus ? `${focus} READING` : "TAROT READING"}
                      </h2>
                      {(selectedZodiac || selectedRisingSign) && (
                        <p className="text-sm text-gray-600 mb-6 italic">
                          Influenced by{" "}
                          {selectedZodiac && (
                            <>
                              <span className="font-bold">Sun:</span>{" "}
                              <span className="text-custom-red">
                                {selectedZodiac}
                              </span>
                            </>
                          )}
                          {selectedZodiac && selectedRisingSign && " & "}
                          {selectedRisingSign && (
                            <>
                              <span className="font-bold">Rising:</span>{" "}
                              <span className="text-custom-red">
                                {selectedRisingSign}
                              </span>
                            </>
                          )}
                        </p>
                      )}

                      <div className="mb-8 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => {
                            setShowFullReading(!showFullReading);
                            if (!fullReading && !showFullReading) {
                              getFullReading(); // Calls with signs from store
                            }
                          }}
                          className="inline-block px-6 py-3 bg-custom-red text-white font-medium text-sm uppercase shadow-md hover:bg-red-700 hover:shadow-lg focus:outline-none transition duration-150 ease-in-out rounded-none"
                        >
                          {showFullReading
                            ? "Hide Full Reading"
                            : "GET FULL IN-DEPTH READING"}
                        </button>
                        <p className="mt-2 text-sm text-black">
                          {showFullReading
                            ? "Return to the concise reading."
                            : "Unlock a detailed analysis with personalized insights and spiritual guidance."}
                        </p>
                      </div>

                      {showFullReading && (
                        <motion.div
                          className="mt-6 p-6 bg-gray-50 border border-gray-200 mb-8"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5 }}
                        >
                          {isLoadingFullReading ? (
                            <div className="text-center py-8">
                              <p className="text-gray-600 italic mb-3">
                                Conjuring your in-depth reading...
                              </p>
                              <div className="flex justify-center">
                                <div className="animate-spin h-10 w-10 border-b-2 border-custom-red"></div>
                              </div>
                            </div>
                          ) : (
                            <div className="prose prose-lg max-w-none">
                              {fullReading.split("\n").map((line, index) => {
                                line = line.trim();
                                if (!line) return null;

                                // Handle title
                                if (line.startsWith("# ")) {
                                  let headingText = line.substring(2).trim();
                                  if (
                                    headingText
                                      .toLowerCase()
                                      .startsWith("in-depth") &&
                                    focus
                                  ) {
                                    headingText = `In-Depth ${focus} Reading`;
                                  } else if (
                                    headingText
                                      .toLowerCase()
                                      .startsWith("in-depth")
                                  ) {
                                    headingText = `In-Depth Tarot Reading`;
                                  }
                                  return (
                                    <h1
                                      key={index}
                                      className="text-2xl font-bold mt-4 mb-6"
                                    >
                                      {headingText}
                                    </h1>
                                  );
                                }

                                // Handle section headers
                                const headerMatch = line.match(
                                  /^\*\*(Past|Present|Future|Integration and Guidance|Personal Astrological Influence):\s*(.*)\*\*$/i
                                );
                                if (headerMatch) {
                                  return (
                                    <h2
                                      key={index}
                                      className="mt-6 mb-3 text-xl font-semibold text-gray-800"
                                    >
                                      {line.replace(/\*\*/g, "")}
                                    </h2>
                                  );
                                }

                                // Handle potential intro paragraph (must come after title/header checks)
                                const isIntroParagraph =
                                  index > 0 &&
                                  !fullReading
                                    .split("\n")
                                    .slice(0, index)
                                    .some((prevLine) => {
                                      const trimmedPrev = prevLine.trim();
                                      return (
                                        trimmedPrev &&
                                        !trimmedPrev.startsWith("#") &&
                                        !trimmedPrev.startsWith("**")
                                      );
                                    });
                                if (isIntroParagraph) {
                                  return (
                                    <p
                                      key={index}
                                      className="mb-6 text-lg italic text-gray-600"
                                    >
                                      {line}
                                    </p>
                                  );
                                }

                                // Default paragraph handling
                                return (
                                  <p key={index} className="mb-4 text-gray-700">
                                    {line}
                                  </p>
                                );
                              })}
                            </div>
                          )}
                        </motion.div>
                      )}

                      <div className="prose prose-lg max-w-none">
                        {aiReading.split("\n\n").map((paragraph, index) => {
                          paragraph = paragraph.trim();

                          if (/^\[.+\]\s+Reading$/i.test(paragraph)) {
                            return null;
                          }

                          if (paragraph.startsWith("**PAST:**")) {
                            const content = paragraph
                              .replace("**PAST:**", "")
                              .trim();
                            return (
                              <div key={index} className="mb-5">
                                <span className="font-bold uppercase text-gray-800 mr-2">
                                  PAST:
                                </span>
                                <span className="text-gray-700">{content}</span>
                              </div>
                            );
                          }

                          if (paragraph.startsWith("**PRESENT:**")) {
                            const content = paragraph
                              .replace("**PRESENT:**", "")
                              .trim();
                            return (
                              <div key={index} className="mb-5">
                                <span className="font-bold uppercase text-gray-800 mr-2">
                                  PRESENT:
                                </span>
                                <span className="text-gray-700">{content}</span>
                              </div>
                            );
                          }

                          if (paragraph.startsWith("**FUTURE:**")) {
                            const content = paragraph
                              .replace("**FUTURE:**", "")
                              .trim();
                            return (
                              <div key={index} className="mb-5">
                                <span className="font-bold uppercase text-gray-800 mr-2">
                                  FUTURE:
                                </span>
                                <span className="text-gray-700">{content}</span>
                              </div>
                            );
                          }

                          if (paragraph.startsWith("**In summary:**")) {
                            const content = paragraph
                              .replace("**In summary:**", "")
                              .trim();
                            return (
                              <div
                                key={index}
                                className="mt-6 mb-5 pt-4 border-t border-gray-200"
                              >
                                <span className="font-bold text-gray-800 mr-2">
                                  In summary:
                                </span>
                                <span className="text-gray-700">{content}</span>
                              </div>
                            );
                          }

                          return (
                            <p
                              key={index}
                              className="mb-4 text-gray-700 leading-relaxed"
                            >
                              {paragraph}
                            </p>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
